import { Campaign, AdGroup, Keyword } from '@bingads-webui/campaign-odata-schemas';
import { filter, isUndefined, isEmpty, each, isArray, where, contains, map } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { getODataErrors } from '@bingads-webui-campaign/api-errors';

import { isBulkWrappedResponse } from '../shared/bulk';
import {
  ERROR_ENTITY,
  getBulkUploadErrorType,
  modelToViewModelForBulkErrors,
  removeReferencedEntityInvalidIdErrors,
} from '../shared/bulk-upsert-errors';
import { GENERIC_ERROR_MESSAGE_KEY } from '../shared/constants';

const notEnoughText = '20000';
const tooMuchText = '20001';
const adGroupEDM = edmCampaign.types.resolve('Model.AdGroup');
const adGroupResource = new adGroupEDM.ResourceIdentifier();

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
  [getBulkUploadErrorType(Keyword)]: ERROR_ENTITY.Keyword,
};

export const getKeyFromKeyword = (keyword) => {
  switch (keyword.MatchType) {
    case 'Broad': return keyword.Text;
    case 'Phrase': return `"${keyword.Text}"`;
    case 'Exact': return `[${keyword.Text}]`;
    default: return keyword.Text;
  }
};

const handleBulkApiError = (error, entity) => {
  const clientIdToKeywordMap = {};
  if (!isEmpty(entity)) {
    const filteredKeywords = filter(entity.Keywords, keyword => isUndefined(keyword.Id));
    each(filteredKeywords, (keyword) => {
      clientIdToKeywordMap[keyword['Client Id']] = keyword;
    });
  }

  const globalLegacyI18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');
  const s$TextAd = window.globalLegacySchema.get('TextAd');
  const getNameAndLengthForProperty = property => ({
    name: globalLegacyI18n.get(`ColumnName_${property}`),
    length: s$TextAd.properties[property].maxLength,
  });
  const escapeHtml = text => (text.replace(/</g, '&lt;').replace(/>/g, '&gt;'));

  if (error && error.clientId && !isUndefined(clientIdToKeywordMap[error.clientId])) {
    const errorFromAd = contains(['Title', 'Text', 'DestinationUrl'], error.property);
    let errorMessage = error.message;

    if (errorFromAd && error.code === tooMuchText && error.message.indexOf('{0}') > -1 && error.message.indexOf('{1}') > -1) {
      const pairs = getNameAndLengthForProperty(error.property);
      errorMessage = error.message.replace('{0}', pairs.name).replace('{1}', pairs.length);
    }
    if (errorFromAd && error.code === notEnoughText && error.message.indexOf('{0}') > -1) {
      errorMessage = error.message.replace('{0}', getNameAndLengthForProperty(error.property).name);
    }

    errorMessage = globalLegacyI18n.getString('Keywords_And_Errors_seperator', escapeHtml(getKeyFromKeyword(clientIdToKeywordMap[error.clientId])), errorMessage);

    if (errorMessage.indexOf('<a href=') !== -1 || (error && error.isHtmlMessage)) {
      errorMessage = {
        ...error,
        DangerousHtmlMessage: errorMessage,
      };
    }
    // eslint-disable-next-line no-param-reassign
    error.message = errorMessage;
  }
  return error;
};

export const saveKeyword = async ({
  campaignId,
  adGroupId,
  keywords,
}, {
  scenario, i18n, activityLogger,
}) => {
  const modelToSave = {
    Keywords: keywords,
    Id: adGroupId,
    Campaign: {
      Id: campaignId,
    },
  };

  try {
    const result = await adGroupResource.bulkUpdate.bind(adGroupResource)(modelToSave);

    return result;
  } catch (bulkOrSystemError) {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity, errors } = bulkOrSystemError;

      let filteredErrors = errors;
      if (entity.Id <= 0) {
        filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
      }

      let errorList = [];
      if (isArray(entity.Keywords)) {
        each(entity.Keywords, (keyword) => {
          if (keyword['Client Id'] && !keyword.Id) {
            const errorsOfThisKeyword = where(filteredErrors, { 'Client Id': keyword['Client Id'] });
            errorList = errorList.concat(errorsOfThisKeyword);
          }
        });
      }

      const errorProcessor = error => handleBulkApiError(error, entity);

      const serverErrors = modelToViewModelForBulkErrors(
        errorList,
        bulkUpsertEntityErrorMap,
        scenario,
        i18n,
        activityLogger,
        errorProcessor
      );

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        serverErrors,
        entity,
      });
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`AdGroup bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);

    if (bulkOrSystemError.status === 400) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        systemErrors: map(getODataErrors(bulkOrSystemError.errors, i18n), error => error.message),
      });
    }

    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
    });
  }
};
